import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ExpenseStatusBadge } from '../ui/ExpenseStatusBadge';
import { RejectExpenseModal } from './RejectExpenseModal';
import { Avatar } from '../ui/Avatar';
import { DollarSignIcon, CalendarIcon, MapPinIcon, FileTextIcon, UserIcon, ClipboardIcon, CheckCircleIcon, XCircleIcon, ClockIcon, ExternalLinkIcon, MessageSquareIcon, AlertTriangleIcon, FlagIcon, InfoIcon } from 'lucide-react';
import { mockEmployees, mockPerDiemRates } from '../../utils/mockData';
import { formatCurrency, formatDateTime } from '../../utils/formatUtils';
interface ExpenseDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  expense: any;
  isAdmin?: boolean;
  onApprove?: (id: number) => void;
  onReject?: (id: number, reason: string) => void;
}
export function ExpenseDetailModal({
  isOpen,
  onClose,
  expense,
  isAdmin = false,
  onApprove,
  onReject
}: ExpenseDetailModalProps) {
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  if (!expense) return null;
  // Get employee name by ID
  const getEmployeeName = id => {
    const employee = mockEmployees.find(emp => emp.id === id);
    return employee ? employee.name : 'Unknown Employee';
  };
  // Get employee by ID
  const getEmployee = id => {
    return mockEmployees.find(emp => emp.id === id) || null;
  };
  // Get per diem rate for the expense
  const getPerDiemRate = () => {
    if (expense.type === 'meal' && expense.subtype) {
      const perDiem = mockPerDiemRates.getPerDiemRateForDate(expense.location, new Date(expense.date));
      if (perDiem) {
        return perDiem.mealBreakdown[expense.subtype];
      }
    } else if (expense.type === 'lodging') {
      const perDiem = mockPerDiemRates.getPerDiemRateForDate(expense.location, new Date(expense.date));
      if (perDiem) {
        return perDiem.lodgingRate;
      }
    }
    return null;
  };
  // Check if expense exceeds per diem
  const isExceedingPerDiem = () => {
    const perDiemRate = getPerDiemRate();
    if (perDiemRate !== null && expense.amount > perDiemRate) {
      return true;
    }
    return false;
  };
  // Handle approve expense
  const handleApprove = () => {
    if (onApprove) {
      onApprove(expense.id);
    }
  };
  // Handle reject expense (opens modal)
  const handleReject = () => {
    setIsRejectModalOpen(true);
  };
  // Handle reject submission
  const handleRejectSubmit = reason => {
    if (onReject) {
      onReject(expense.id, reason);
    }
    setIsRejectModalOpen(false);
  };
  // Handle request information
  const handleRequestInfo = () => {
    setIsMessageModalOpen(true);
  };
  // Handle message submission
  const handleMessageSubmit = () => {
    // In a real app, this would send a message to the employee
    alert(`Message sent to employee: ${message}`);
    setIsMessageModalOpen(false);
    setMessage('');
  };
  // Get employee data
  const employee = getEmployee(expense.employeeId);
  return <>
      <Modal isOpen={isOpen} onClose={onClose} title="Expense Details" size="lg" data-id="element-582">
        <div className="p-6" data-id="element-583">
          {/* Header with status */}
          <div className="flex justify-between items-start mb-6" data-id="element-584">
            <div data-id="element-585">
              <h3 className="text-xl font-semibold text-gray-100 capitalize" data-id="element-586">
                {expense.type} Expense
                {expense.subtype && ` - ${expense.subtype}`}
              </h3>
              <p className="text-gray-400 mt-1" data-id="element-587">
                Submitted on {formatDateTime(expense.submittedDate, true)}
              </p>
            </div>
            <ExpenseStatusBadge status={expense.status} size="lg" data-id="element-588" />
          </div>
          {/* Main content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-589">
            {/* Left column - Basic info */}
            <div className="space-y-4" data-id="element-590">
              <div data-id="element-591">
                <h4 className="text-sm font-medium text-gray-400 mb-1" data-id="element-592">
                  Amount
                </h4>
                <div className="flex items-center" data-id="element-593">
                  <DollarSignIcon size={16} className="text-gray-500 mr-2" data-id="element-594" />
                  <span className="text-lg font-semibold text-gray-100" data-id="element-595">
                    {formatCurrency(expense.amount)}
                  </span>
                </div>
              </div>
              <div data-id="element-596">
                <h4 className="text-sm font-medium text-gray-400 mb-1" data-id="element-597">
                  Expense Date
                </h4>
                <div className="flex items-center" data-id="element-598">
                  <CalendarIcon size={16} className="text-gray-500 mr-2" data-id="element-599" />
                  <span className="text-gray-200" data-id="element-600">
                    {formatDateTime(expense.date, true)}
                  </span>
                </div>
              </div>
              <div data-id="element-601">
                <h4 className="text-sm font-medium text-gray-400 mb-1" data-id="element-602">
                  Location
                </h4>
                <div className="flex items-center" data-id="element-603">
                  <MapPinIcon size={16} className="text-gray-500 mr-2" data-id="element-604" />
                  <span className="text-gray-200" data-id="element-605">
                    {expense.location || 'N/A'}
                  </span>
                </div>
              </div>
              {expense.type === 'meal' && expense.attendees && expense.attendees.length > 0 && <div data-id="element-606">
                    <h4 className="text-sm font-medium text-gray-400 mb-1" data-id="element-607">
                      Attendees
                    </h4>
                    <div className="flex items-start" data-id="element-608">
                      <UserIcon size={16} className="text-gray-500 mr-2 mt-0.5" data-id="element-609" />
                      <div data-id="element-610">
                        {expense.attendees.map((attendee, index) => <div key={index} className="text-gray-200" data-id="element-611">
                            {attendee}
                          </div>)}
                      </div>
                    </div>
                  </div>}
              {/* Per Diem Information */}
              {getPerDiemRate() !== null && <div className={`p-3 rounded-md ${isExceedingPerDiem() ? 'bg-red-900/30 border border-red-800' : 'bg-gray-750 border border-gray-700'}`} data-id="element-612">
                  <h4 className={`text-sm font-medium mb-1 ${isExceedingPerDiem() ? 'text-red-300' : 'text-gray-300'}`} data-id="element-613">
                    Per Diem Allowance
                  </h4>
                  <div className="flex items-center" data-id="element-614">
                    {isExceedingPerDiem() ? <AlertTriangleIcon size={16} className="text-red-400 mr-2" data-id="element-615" /> : <InfoIcon size={16} className="text-gray-500 mr-2" data-id="element-616" />}
                    <span className={`text-sm ${isExceedingPerDiem() ? 'text-red-300' : 'text-gray-300'}`} data-id="element-617">
                      {isExceedingPerDiem() ? `Exceeds allowance of ${formatCurrency(getPerDiemRate())}` : `Allowance: ${formatCurrency(getPerDiemRate())}`}
                    </span>
                  </div>
                </div>}
            </div>
            {/* Middle column - Description and status timeline */}
            <div className="space-y-4" data-id="element-618">
              <div data-id="element-619">
                <h4 className="text-sm font-medium text-gray-400 mb-1" data-id="element-620">
                  Description
                </h4>
                <div className="p-3 bg-gray-750 border border-gray-700 rounded-md" data-id="element-621">
                  <p className="text-gray-200 whitespace-pre-wrap" data-id="element-622">
                    {expense.description}
                  </p>
                </div>
              </div>
              <div data-id="element-623">
                <h4 className="text-sm font-medium text-gray-400 mb-3" data-id="element-624">
                  Status Timeline
                </h4>
                <div className="space-y-3" data-id="element-625">
                  <div className="flex items-start" data-id="element-626">
                    <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-900 flex items-center justify-center mt-0.5 mr-3" data-id="element-627">
                      <ClipboardIcon size={12} className="text-blue-300" data-id="element-628" />
                    </div>
                    <div data-id="element-629">
                      <p className="text-sm font-medium text-gray-200" data-id="element-630">
                        Submitted
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-631">
                        {formatDateTime(expense.submittedDate, true)}
                      </p>
                    </div>
                  </div>
                  {expense.status !== 'pending' && <div className="flex items-start" data-id="element-632">
                      <div className={`flex-shrink-0 h-5 w-5 rounded-full mt-0.5 mr-3 flex items-center justify-center ${expense.status === 'approved' ? 'bg-green-900' : expense.status === 'rejected' ? 'bg-red-900' : 'bg-orange-900'}`} data-id="element-633">
                        {expense.status === 'approved' && <CheckCircleIcon size={12} className="text-green-300" data-id="element-634" />}
                        {expense.status === 'rejected' && <XCircleIcon size={12} className="text-red-300" data-id="element-635" />}
                        {expense.status === 'flagged' && <FlagIcon size={12} className="text-orange-300" data-id="element-636" />}
                      </div>
                      <div data-id="element-637">
                        <p className="text-sm font-medium text-gray-200 capitalize" data-id="element-638">
                          {expense.status}
                        </p>
                        <p className="text-xs text-gray-400" data-id="element-639">
                          {expense.status === 'approved' && expense.approvedDate && formatDateTime(expense.approvedDate, true)}
                          {expense.status === 'rejected' && expense.rejectedDate && formatDateTime(expense.rejectedDate, true)}
                          {expense.status === 'flagged' && expense.flaggedDate && formatDateTime(expense.flaggedDate, true)}
                        </p>
                        {/* Rejection reason */}
                        {expense.status === 'rejected' && expense.rejectionReason && <div className="mt-2 p-2 bg-red-900/30 border border-red-800 rounded text-xs text-red-200" data-id="element-640">
                              Reason: {expense.rejectionReason}
                            </div>}
                      </div>
                    </div>}
                  {expense.status === 'pending' && <div className="flex items-start opacity-50" data-id="element-641">
                      <div className="flex-shrink-0 h-5 w-5 rounded-full bg-gray-700 flex items-center justify-center mt-0.5 mr-3" data-id="element-642">
                        <ClockIcon size={12} className="text-gray-400" data-id="element-643" />
                      </div>
                      <div data-id="element-644">
                        <p className="text-sm font-medium text-gray-400" data-id="element-645">
                          Awaiting Approval
                        </p>
                      </div>
                    </div>}
                </div>
              </div>
            </div>
            {/* Right column - Receipt and actions */}
            <div className="space-y-4" data-id="element-646">
              <div data-id="element-647">
                <h4 className="text-sm font-medium text-gray-400 mb-1" data-id="element-648">
                  Receipt
                </h4>
                {expense.receiptUrl ? <div className="bg-gray-750 border border-gray-700 rounded-md p-3" data-id="element-649">
                    <div className="aspect-[4/3] bg-gray-800 rounded flex items-center justify-center mb-2" data-id="element-650">
                      <img src={expense.receiptUrl} alt="Receipt" className="max-h-full max-w-full object-contain rounded" onError={e => {
                    e.target.onerror = null;
                    e.target.src = 'https://placehold.co/400x300/1f2937/6b7280?text=Receipt+Image';
                  }} data-id="element-651" />
                    </div>
                    <a href={expense.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 flex items-center justify-center" data-id="element-652">
                      View Full Receipt
                      <ExternalLinkIcon size={12} className="ml-1" data-id="element-653" />
                    </a>
                  </div> : <div className="bg-gray-750 border border-gray-700 rounded-md p-4 flex items-center justify-center" data-id="element-654">
                    <span className="text-gray-400 text-sm" data-id="element-655">
                      No receipt provided
                    </span>
                  </div>}
              </div>
              {/* Employee info (for admin view) */}
              {isAdmin && employee && <div className="bg-gray-750 border border-gray-700 rounded-md p-3" data-id="element-656">
                  <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-657">
                    Employee Information
                  </h4>
                  <div className="flex items-center" data-id="element-658">
                    <Avatar name={employee.name} id={employee.id} size="md" data-id="element-659" />
                    <div className="ml-3" data-id="element-660">
                      <div className="text-sm font-medium text-gray-200" data-id="element-661">
                        {employee.name}
                      </div>
                      <div className="text-xs text-gray-400" data-id="element-662">
                        {employee.department} • {employee.position}
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Admin actions */}
              {isAdmin && expense.status === 'pending' && <div className="space-y-2" data-id="element-663">
                  <Button variant="success" className="w-full" onClick={handleApprove} data-id="element-664">
                    <CheckCircleIcon size={16} className="mr-1.5" data-id="element-665" />
                    Approve Expense
                  </Button>
                  <Button variant="destructive" className="w-full" onClick={handleReject} data-id="element-666">
                    <XCircleIcon size={16} className="mr-1.5" data-id="element-667" />
                    Reject Expense
                  </Button>
                  <Button variant="outline" className="w-full" onClick={handleRequestInfo} data-id="element-668">
                    <MessageSquareIcon size={16} className="mr-1.5" data-id="element-669" />
                    Request Information
                  </Button>
                </div>}
              {/* Employee actions */}
              {!isAdmin && expense.status === 'rejected' && <Button className="w-full" data-id="element-670">
                  <ClipboardIcon size={16} className="mr-1.5" data-id="element-671" />
                  Resubmit Expense
                </Button>}
            </div>
          </div>
        </div>
        <div className="bg-gray-750 px-6 py-4 flex justify-end border-t border-gray-700" data-id="element-672">
          <Button variant="outline" onClick={onClose} data-id="element-673">
            Close
          </Button>
        </div>
      </Modal>
      {/* Reject Modal */}
      <RejectExpenseModal isOpen={isRejectModalOpen} onClose={() => setIsRejectModalOpen(false)} onSubmit={handleRejectSubmit} expense={expense} data-id="element-674" />
      {/* Request Information Modal */}
      <Modal isOpen={isMessageModalOpen} onClose={() => setIsMessageModalOpen(false)} title="Request Additional Information" size="md" data-id="element-675">
        <div className="p-6" data-id="element-676">
          <div className="mb-4" data-id="element-677">
            <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-678">
              Message to Employee
            </label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" placeholder="Specify what additional information you need from the employee..." data-id="element-679"></textarea>
          </div>
          <div className="bg-blue-900/30 border border-blue-800 rounded-md p-4 flex items-start mb-6" data-id="element-680">
            <InfoIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-681" />
            <div data-id="element-682">
              <p className="text-sm text-blue-200" data-id="element-683">
                The employee will receive a notification and the expense will
                remain in "Pending" status until they respond.
              </p>
            </div>
          </div>
          <div className="flex justify-end space-x-3" data-id="element-684">
            <Button type="button" variant="outline" onClick={() => setIsMessageModalOpen(false)} data-id="element-685">
              Cancel
            </Button>
            <Button type="button" onClick={handleMessageSubmit} disabled={!message.trim()} data-id="element-686">
              <MessageSquareIcon size={16} className="mr-1.5" data-id="element-687" />
              Send Request
            </Button>
          </div>
        </div>
      </Modal>
    </>;
}